import { Card, Button, Typography } from "@altimateai/lego";
import {
  ArrowRightIcon,
  DatabaseIcon,
  GitMergeIcon,
  CodeIcon,
  ArrowLeftIcon,
} from "../../../assets/icons";
import { useState } from "react";
import { ChatProvider, ChatSession } from "../../chatbotV2/components";
import { TaskLabels, TeamMateComponentProps } from "../types";
import HelloMessage from "./HelloMessage";
import { Stack } from "@altimateai/lego";

const TeradataToSnowflakeMigratorDisplay = ({
  urls,
}: TeamMateComponentProps) => {
  const [isStarted, setIsStarted] = useState(false);

  const handleStart = () => {
    setIsStarted(true);
  };

  const handleBack = () => {
    setIsStarted(false);
  };

  const validateRequest = async (data: { context: ChatSession["context"] }) => {
    if (!data.context?.query) {
      throw new Error("Missing query");
    }
  };

  return (
    <div className="al-relative al-overflow-hidden">
      {/* Wrapper for sliding animation */}
      <Stack
        className={`al-transition-transform al-duration-500 al-ease-in-out ${
          isStarted ? "al--translate-x-full" : "al-translate-x-0"
        }`}
      >
        {/* Landing Content */}
        <div className="al-w-full al-flex-shrink-0">
          <div className="al-px-2 al-py-2 al-max-w-4xl">
            {/* Hero Section */}
            <Card className="al-mb-12 al-overflow-hidden al-relative al-bg-gradient-to-r al-from-primary/10 al-to-primary/5">
              <Stack
                direction="column"
                className="md:al-flex-row al-items-center al-gap-8 al-p-8"
              >
                {/* AI Agent Visual */}
                <div className="al-w-48 al-h-48 al-relative">
                  <div className="al-absolute al-inset-0 al-bg-primary/20 al-rounded-full al-animate-pulse"></div>
                  <div className="al-absolute al-inset-4 al-bg-primary/30 al-rounded-full al-animate-pulse [animation-delay:0.2s]"></div>
                  <div className="al-absolute al-inset-8 al-bg-primary/40 al-rounded-full al-animate-pulse [animation-delay:0.4s]"></div>
                  <Stack className="al-absolute al-inset-12 al-bg-primary al-rounded-full al-items-center al-justify-center">
                    <span className="al-text-white al-text-4xl">🤖</span>
                  </Stack>
                </div>

                {/* Content */}
                <div className="al-flex-1 al-text-left">
                  <Typography variant="h4" className="al-mb-4">
                    Your AI Migration Companion
                  </Typography>
                  <p className="al-text-lg al-text-muted-foreground al-mb-6">
                    Let me guide you through a seamless Teradata to Snowflake
                    transformation. Intelligent, efficient, and error-free
                    migrations await.
                  </p>
                  <Button
                    size="lg"
                    className="al-gap-2 al-bg-primary/90 hover:al-bg-primary"
                    onClick={handleStart}
                  >
                    Start Migration <ArrowRightIcon className="al-h-4 al-w-4" />
                  </Button>
                </div>
              </Stack>
            </Card>

            {/* Features Timeline */}
            <div className="al-relative al-mb-12">
              {/* Connection Line */}
              <div className="al-absolute al-left-1/2 al-top-0 al-bottom-0 al-w-0.5 al-bg-primary/20 al-transform al-translate-x-[-50%]"></div>

              {/* Feature Steps */}
              <div className="al-space-y-12">
                {/* Schema Migration Step */}
                <Stack align="center" className="al-gap-8">
                  <div className="al-flex-1 al-text-right">
                    <Card className="al-inline-block al-p-4">
                      <h3 className="al-text-xl al-font-semibold al-mb-2">
                        <Stack align="center" className="al-gap-2">
                          <span>
                            <DatabaseIcon className="al-h-6 al-w-6 al-text-primary" />
                            Schema Migration
                          </span>
                        </Stack>
                      </h3>
                      <p className="al-text-muted-foreground al-m-0">
                        Automatically convert Teradata schemas to
                        Snowflake-compatible formats while preserving data
                        structures
                      </p>
                    </Card>
                  </div>
                  <Stack className="al-w-12 al-h-12 al-rounded-full al-bg-primary al-items-center al-justify-center al-relative al-z-10">
                    <span className="al-text-white">1</span>
                  </Stack>
                  <div className="al-flex-1"></div>
                </Stack>

                {/* Data Transfer Step */}
                <Stack align="center" className="al-gap-8">
                  <div className="al-flex-1"></div>
                  <Stack className="al-w-12 al-h-12 al-rounded-full al-bg-primary al-items-center al-justify-center al-relative al-z-10">
                    <span className="al-text-white">2</span>
                  </Stack>
                  <div className="al-flex-1">
                    <Card className="al-inline-block al-p-4">
                      <h3 className="al-text-xl al-font-semibold al-mb-2">
                        <Stack align="center" className="al-gap-2">
                          <span>
                            <GitMergeIcon className="al-h-6 al-w-6 al-text-primary" />
                            Data Transfer
                          </span>
                        </Stack>
                      </h3>
                      <p className="al-text-muted-foreground al-m-0">
                        Secure and efficient data migration with progress
                        tracking and validation
                      </p>
                    </Card>
                  </div>
                </Stack>

                {/* Query Conversion Step */}
                <Stack align="center" className="al-gap-8">
                  <div className="al-flex-1 al-text-right">
                    <Card className="al-inline-block al-p-4">
                      <h3 className="al-text-xl al-font-semibold al-mb-2">
                        <Stack align="center" className="al-gap-2">
                          <span>
                            <CodeIcon className="al-h-6 al-w-6 al-text-primary" />
                            Query Conversion
                          </span>
                        </Stack>
                      </h3>
                      <p className="al-text-muted-foreground al-m-0">
                        Transform Teradata SQL queries to Snowflake syntax with
                        intelligent optimization
                      </p>
                    </Card>
                  </div>
                  <Stack className="al-w-12 al-h-12 al-rounded-full al-bg-primary al-items-center al-justify-center al-relative al-z-10">
                    <span className="al-text-white">3</span>
                  </Stack>
                  <div className="al-flex-1"></div>
                </Stack>
              </div>
            </div>

            {/* Benefits Section */}
            <Card className="al-p-6 al-mb-12 al-bg-gradient-to-b al-from-primary/5">
              <Typography variant="h4" className="al-mb-6 al-text-center">
                AI-Powered Advantages
              </Typography>
              <div className="al-grid al-grid-cols-2 al-gap-6">
                <Stack align="start" className="al-gap-3">
                  <Stack className="al-h-8 al-w-8 al-items-center al-justify-center al-bg-primary/10 al-rounded-lg">
                    ✨
                  </Stack>
                  <div>
                    <h4 className="al-font-semibold al-mb-1">
                      Smart Conversion
                    </h4>
                    <p className="al-text-sm al-text-muted-foreground">
                      AI-powered conversion ensures accurate and optimized
                      migrations
                    </p>
                  </div>
                </Stack>
                <Stack align="start" className="al-gap-3">
                  <Stack className="al-h-8 al-w-8 al-items-center al-justify-center al-bg-primary/10 al-rounded-lg">
                    🚀
                  </Stack>
                  <div>
                    <h4 className="al-font-semibold al-mb-1">Lightning Fast</h4>
                    <p className="al-text-sm al-text-muted-foreground">
                      Significantly reduce migration time and effort
                    </p>
                  </div>
                </Stack>
                <Stack align="start" className="al-gap-3">
                  <Stack className="al-h-8 al-w-8 al-items-center al-justify-center al-bg-primary/10 al-rounded-lg">
                    🛡️
                  </Stack>
                  <div>
                    <h4 className="al-font-semibold al-mb-1">
                      Reliable & Secure
                    </h4>
                    <p className="al-text-sm al-text-muted-foreground">
                      Built-in validation and error handling for reliable
                      migrations
                    </p>
                  </div>
                </Stack>
                <Stack align="start" className="al-gap-3">
                  <Stack className="al-h-8 al-w-8 al-items-center al-justify-center al-bg-primary/10 al-rounded-lg">
                    📊
                  </Stack>
                  <div>
                    <h4 className="al-font-semibold al-mb-1">
                      Full Visibility
                    </h4>
                    <p className="al-text-sm al-text-muted-foreground">
                      Detailed reporting and progress tracking throughout the
                      process
                    </p>
                  </div>
                </Stack>
              </div>
            </Card>

            {/* CTA Section */}
            <Card className="al-text-center al-p-8 al-bg-primary/5">
              <Typography variant="h4" className="al-mb-4">
                Ready to Transform Your Database?
              </Typography>
              <p className="al-text-muted-foreground al-mb-6">
                Start your intelligent migration journey today
              </p>
              <Button size="lg" className="al-gap-2" onClick={handleStart}>
                Begin Migration <ArrowRightIcon className="al-h-4 al-w-4" />
              </Button>
            </Card>
          </div>
        </div>

        {/* Migration Interface */}
        <div className="al-w-full al-flex-shrink-0">
          <div className="al-h-full al-w-full">
            <Button
              variant="ghost"
              size="icon"
              title="Back"
              onClick={handleBack}
              className="al-h-8 al-w-8 al-ml-4"
            >
              <ArrowLeftIcon className="al-h-4 al-w-4" />
              Back
            </Button>
            <ChatProvider
              initialValidation={validateRequest}
              taskLabel={TaskLabels.TeradataToSnowflakeMigrator}
              helloMessage={<HelloMessage />}
              fileUploadProps={{
                allowedFiles: ".sql",
                contextFieldKey: "query",
              }}
              urls={urls}
            />
          </div>
        </div>
      </Stack>
    </div>
  );
};

export default TeradataToSnowflakeMigratorDisplay;
